import React, { useState } from 'react';
import { X, Send, Users, Plus, Trash2, Mail } from 'lucide-react';

interface EmailTemplate {
  template_id: number;
  template_name: string;
  recipient_type: string;
  subject_template: string;
  body_template: string;
  weeks_ahead: number;
  include_metadata: boolean;
  music_folder_paths: string[];
}

interface BatchEmailItem {
  templateId: number;
  recipients: string[];
}

interface BatchEmailModalProps {
  templates: EmailTemplate[];
  onSend: (batchData: any) => void;
  onCancel: () => void;
}

const BatchEmailModal: React.FC<BatchEmailModalProps> = ({ 
  templates, 
  onSend, 
  onCancel 
}) => {
  const [batchItems, setBatchItems] = useState<BatchEmailItem[]>([
    { templateId: templates[0]?.template_id || 0, recipients: [''] }
  ]);
  const [sending, setSending] = useState(false);

  const addBatchItem = () => {
    setBatchItems([...batchItems, { templateId: 0, recipients: [''] }]);
  };

  const removeBatchItem = (index: number) => {
    if (batchItems.length > 1) {
      setBatchItems(batchItems.filter((_, i) => i !== index));
    }
  };

  const updateBatchItem = (index: number, updates: Partial<BatchEmailItem>) => {
    const newItems = [...batchItems];
    newItems[index] = { ...newItems[index], ...updates };
    setBatchItems(newItems);
  };

  const addRecipient = (batchIndex: number) => {
    const newItems = [...batchItems];
    newItems[batchIndex].recipients.push('');
    setBatchItems(newItems);
  };

  const updateRecipient = (batchIndex: number, recipientIndex: number, value: string) => {
    const newItems = [...batchItems];
    newItems[batchIndex].recipients[recipientIndex] = value;
    setBatchItems(newItems);
  };

  const removeRecipient = (batchIndex: number, recipientIndex: number) => {
    const newItems = [...batchItems];
    if (newItems[batchIndex].recipients.length > 1) {
      newItems[batchIndex].recipients.splice(recipientIndex, 1);
      setBatchItems(newItems);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const validItems = batchItems
        .filter(item => item.templateId > 0)
        .map(item => ({
          templateId: item.templateId,
          recipients: item.recipients.filter(r => r.trim() !== '')
        }))
        .filter(item => item.recipients.length > 0);

      const batchData = {
        templates: validItems
      };

      await onSend(batchData);
    } catch (error) {
      console.error('Error sending batch emails:', error);
    } finally {
      setSending(false);
    }
  };

  const getSelectedTemplate = (templateId: number) => {
    return templates.find(t => t.template_id === templateId);
  };

  const totalRecipients = batchItems.reduce((total, item) => {
    return total + item.recipients.filter(r => r.trim() !== '').length;
  }, 0);

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center">
              <Users size={20} className="me-2" />
              Send Batch Emails
            </h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>

          <div className="modal-body">
            <div className="alert alert-info">
              <strong>Batch Email Sending:</strong> Send multiple email templates to different recipient groups in one operation.
              All emails will use the same music files and conflict resolutions.
            </div>

            <div className="mb-3 d-flex align-items-center justify-content-between">
              <div>
                <strong>{batchItems.length}</strong> email templates | 
                <strong className="ms-2">{totalRecipients}</strong> total recipients
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={addBatchItem}
              >
                <Plus size={16} className="me-1" />
                Add Template
              </button>
            </div>

            <div className="batch-items" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {batchItems.map((item, index) => {
                const selectedTemplate = getSelectedTemplate(item.templateId);
                
                return (
                  <div key={index} className="card mb-3">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <h6 className="mb-0">
                          <Mail size={16} className="me-2" />
                          Email Template {index + 1}
                        </h6>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeBatchItem(index)}
                          disabled={batchItems.length === 1}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="card-body">
                      {/* Template Selection */}
                      <div className="mb-3">
                        <label className="form-label">Select Template</label>
                        <select
                          className="form-select"
                          value={item.templateId}
                          onChange={(e) => updateBatchItem(index, { templateId: parseInt(e.target.value) })}
                        >
                          <option value={0}>Choose a template...</option>
                          {templates.map(template => (
                            <option key={template.template_id} value={template.template_id}>
                              {template.template_name} ({template.recipient_type})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Template Info */}
                      {selectedTemplate && (
                        <div className="alert alert-light mb-3">
                          <div className="row">
                            <div className="col-md-6">
                              <strong>Subject:</strong> {selectedTemplate.subject_template}<br />
                              <strong>Type:</strong> <span className="badge bg-primary">{selectedTemplate.recipient_type}</span>
                            </div>
                            <div className="col-md-6">
                              <strong>Weeks Ahead:</strong> {selectedTemplate.weeks_ahead}<br />
                              <strong>Music Files:</strong> {selectedTemplate.music_folder_paths.length > 0 ? `Searching ${selectedTemplate.music_folder_paths.length} folder(s)` : 'No folders configured'}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Recipients */}
                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <label className="form-label mb-0">Recipients</label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => addRecipient(index)}
                          >
                            <Plus size={14} className="me-1" />
                            Add Recipient
                          </button>
                        </div>

                        {item.recipients.map((recipient, recipientIndex) => (
                          <div key={recipientIndex} className="input-group mb-2">
                            <input
                              type="email"
                              className="form-control"
                              placeholder="email@church.com"
                              value={recipient}
                              onChange={(e) => updateRecipient(index, recipientIndex, e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeRecipient(index, recipientIndex)}
                              disabled={item.recipients.length === 1}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="mt-3 p-3 bg-light rounded">
              <h6>Batch Summary:</h6>
              <ul className="mb-0">
                {batchItems.map((item, index) => {
                  const template = getSelectedTemplate(item.templateId);
                  const validRecipients = item.recipients.filter(r => r.trim() !== '');
                  
                  if (!template || validRecipients.length === 0) return null;
                  
                  return (
                    <li key={index}>
                      <strong>{template.template_name}</strong> → {validRecipients.length} recipient(s)
                      <small className="text-muted ms-2">({validRecipients.join(', ')})</small>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="modal-footer">
            <div className="me-auto">
              <small className="text-muted">
                {totalRecipients} total emails will be sent
              </small>
            </div>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              <X size={16} className="me-2" />
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleSend}
              disabled={sending || totalRecipients === 0}
            >
              {sending ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Sending...</span>
                  </div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="me-2" />
                  Send Batch Emails ({totalRecipients})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchEmailModal;